import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as yup from 'yup';

import TextInput from 'components/forms/TextInput/TextInput';
import MultipleSelectField from 'components/MultipleSelectFields';
import RoundedButton from 'components/RoundedButton/RoundedButton';
import Toast from 'components/Toast/Toast';
import useToast from 'hooks/useToast';

import { userRoleOptions } from './constants';
import { queryKeys, useUpdateUser, useMutationAddUser } from './hooks/useUsers';

const roleIdsToOptions = (ids) =>
  (ids || []).map((id) => ({ value: id, label: userRoleOptions.find((o) => o.id === id)?.name || id }));

const AddUser = ({ onClose, selectedUser, params }) => {
  const queryClient = useQueryClient();
  const [isSending, setIsSending] = useState(false);
  const { toast, showSuccess, showError, hideToast } = useToast();
  const { mutate: updateMutate } = useUpdateUser();
  const { mutate: addMutate } = useMutationAddUser();

  const handleEditSuccess = async () => {
    await queryClient.invalidateQueries(queryKeys.users(params), { exact: true });
    setIsSending(false);
    onClose();
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: selectedUser ? selectedUser.fullName : '',
      username: selectedUser ? selectedUser.username : '',
      permissionGroup: selectedUser ? selectedUser.permissionGroup || [] : [],
    },
    validationSchema: yup.object({
      fullName: yup.string().required('Full name is required'),
      username: yup.string().email('Invalid email address').required('Email is required'),
      permissionGroup: yup.array().of(yup.string()).min(1, 'At least one role is required'),
    }),
    onSubmit: (values) => {
      setIsSending(true);
      const { username, ...data } = values;
      if (selectedUser) {
        updateMutate(
          { id: selectedUser.id, data },
          {
            onSuccess: async () => {
              showSuccess(`User ${data.fullName} has been updated successfully`);
              handleEditSuccess();
            },
            onError: () => {
              setIsSending(false);
              showError('Failed to update user. Please try again.');
            },
          }
        );
      } else {
        addMutate(values, {
          onSuccess: async () => {
            showSuccess(`User ${values.fullName} has been added successfully`);
            handleEditSuccess();
          },
          onError: async (err) => {
            setIsSending(false);
            if (err.response?.data?.errors?.description?.message === 'Username already exists') {
              formik.setFieldError('username', 'Username already exists');
            } else {
              showError('Failed to add user. Please try again.');
            }
          },
        });
      }
    },
  });

  const handleRolesChange = (name, selected) => {
    formik.setFieldValue(
      name,
      (selected || []).map((opt) => opt.value)
    );
  };

  const closeHandler = () => {
    if (isSending) {
      return;
    }
    onClose();
    formik.resetForm();
  };

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <div className='m-5'>
        <div className='row'>
          <div className='col-md-12 mb-1'>
            <TextInput
              name='fullName'
              label='Full Name'
              placeholder='John Doe'
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isRequired
              error={formik.touched.fullName && formik.errors.fullName}
            />
          </div>
          <div className='col-md-12 mb-1'>
            <TextInput
              name='username'
              label='Email'
              placeholder='john@doe.com'
              value={formik.values.username}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isDisabled={selectedUser}
              isRequired
              error={formik.touched.username && formik.errors.username}
            />
          </div>
          <div className='col-md-12 mb-9'>
            <MultipleSelectField
              isFormik={false}
              label='User roles'
              name='permissionGroup'
              options={userRoleOptions}
              value={roleIdsToOptions(formik.values.permissionGroup)}
              change={handleRolesChange}
              blur={formik.handleBlur}
              error={formik.touched.permissionGroup && formik.errors.permissionGroup}
              isRequired
            />
          </div>
        </div>
      </div>
      <div className={cx('d-flex', 'justify-content-end gap-2', 'p-3', 'border-t')}>
        <RoundedButton text='cancel' btnStyle='outlined' size='md' onClick={closeHandler} disabled={isSending} />
        <RoundedButton loading={isSending} type='submit' text='Save' btnStyle='contained' color='primary' size='md' />
      </div>
      <Toast {...toast} onClose={hideToast} />
    </form>
  );
};

AddUser.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default AddUser;
